import { countries } from '../constants/countries';
import adminDivisions from '../constants/country-admin-divisions';
import { getFullCountryName, getFullStateName } from './countries';

// Map of all available sources (functions and constants)
const SOURCES = {
  getFullCountryName,
  getFullStateName,
  adminDivisions,
  countries,
  env: {
    supportEmail: process.env.REACT_APP_SUPPORT_EMAIL,
    consentStatement: process.env.REACT_APP_CANDIDATE_CONSENT_STATEMENT,
    policeVettingConsentForm: process.env.REACT_APP_POLICE_VETTING_CONSENT_FORM,
    afpConsentForm: process.env.REACT_APP_AFP_CONSENT_FORM,
    checkmateNameINT: process.env.REACT_APP_CHECKMATE_NAME,
    checkmateNameAU: process.env.REACT_APP_CHECKMATE_NAME_AU,
    checkmateNameUS: process.env.REACT_APP_CHECKMATE_NAME_US,
  },
  // Add other sources here as needed
};

/**
 * Get a nested value from constants by a dotted path that may include bracket lookups.
 * Example: adminDivisions.AU_STATES[VIC].name -> "Victoria"
 */
const resolveTemplatePath = (path, constants) => {
  if (!path || typeof path !== 'string') return null;

  const parts = path.split('.');
  let value = constants?.[parts[0]];
  if (value == null) return null;

  for (let i = 1; i < parts.length; i++) {
    if (value == null) return null;

    // supports [value] lookup (e.g., "AU_STATES[VIC]")
    if (parts[i].includes('[')) {
      const [arrayName, lookup] = parts[i].split('[');
      const lookupValue = lookup.replace(']', '');
      value = value[arrayName]?.find(item => item.value === lookupValue);
      continue;
    }

    value = value[parts[i]];
  }

  return value;
};

/**
 * Replaces simple template variables (path lookups) in strings.
 * Example: {{adminDivisions.AU_STATES[VIC].name}}
 *
 * This handles:
 * 1. Constants from SOURCES (like adminDivisions, countries)
 * 2. Candidate values from sources parameter (like role_location_name, full_name)
 */
const replaceConstantTemplates = (template, sources = {}) => {
  if (typeof template !== 'string' || !template.includes('{{')) return template;

  // escape keys before putting them in a regex alternation
  const escapeRegex = s => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const sourceKeys = Object.keys(sources).map(escapeRegex).join('|');
  if (!sourceKeys) {
    return template;
  }

  const pattern = new RegExp(`{{((?:${sourceKeys})(?:\\.[a-zA-Z0-9_\\[\\]]+)*?)}}`, 'g');

  let result = template;
  let prev;
  let iteration = 0;

  // keep replacing until no further matches
  do {
    prev = result;
    iteration++;
    result = result.replace(pattern, (match, path) => {
      try {
        // first resolve any nested templates in the path string
        const resolvedPath = replaceConstantTemplates(path, sources);
        const value = resolveTemplatePath(resolvedPath, sources);
        return value != null ? String(value) : match;
      } catch (error) {
        return match;
      }
    });

    if (iteration > 10) {
      break;
    }
  } while (result !== prev && result.includes('{{'));

  return result;
};

export const processTemplates = (input, sources = {}, preserveOnNull = false) => {
  if (input == null) return input;

  const sourcesMap = { ...SOURCES, ...sources };

  // If input is a string, process it as a template
  if (typeof input === 'string') {
    // Resolve simple path lookups (e.g., {{full_name}}, {{role_location_name}})
    // These reference hidden field values that are computed by processors
    const result = replaceConstantTemplates(input, sourcesMap);

    // If there are still unresolved templates, preserve them if preserveOnNull is true
    // Otherwise, leave them as-is (they may be resolved later when values are available)
    if (result.includes('{{') && preserveOnNull) {
      return input; // Preserve original template for later processing
    }

    return result;
  }

  // If input is an object/array, recursively process all string values

  if (Array.isArray(input)) {
    return input.map(item => processTemplates(item, sourcesMap));
  }

  if (typeof input === 'object') {
    return Object.entries(input).reduce((acc, [k, v]) => {
      if (typeof v === 'string') {
        // Check if the entire string is just a template variable (preserve arrays)
        // Match {{...}} exactly
        if (v.startsWith('{{') && v.endsWith('}}')) {
          try {
            const templatePath = v.slice(2, -2); // Remove {{ and }}
            const value = resolveTemplatePath(
              replaceConstantTemplates(templatePath, sourcesMap),
              sourcesMap,
            );
            // If it resolves to an array, preserve it; otherwise convert to string
            if (Array.isArray(value)) {
              acc[k] = value;
            } else {
              acc[k] = value != null ? String(value) : v;
            }
          } catch (error) {
            // If resolution fails, keep the original value
            acc[k] = v;
          }
        } else if (v.includes('{{')) {
          // String contains templates - process it
          acc[k] = processTemplates(v, sourcesMap);
        } else {
          // No templates, keep as-is
          acc[k] = v;
        }
      } else if (Array.isArray(v) || (v && typeof v === 'object')) {
        acc[k] = processTemplates(v, sourcesMap);
      } else {
        // Primitive values (number, boolean, null, etc.) - keep as-is
        acc[k] = v;
      }
      return acc;
    }, {});
  }

  return input;
};
